import { format, subDays, startOfMonth, startOfWeek, endOfWeek } from 'date-fns'
import { post } from '../../common/constants'

/**
 * The mail's disclaimer for orders outside the P/L, verbatim (QA: shown after every successful download and as a banner
 * whenever the scope has such orders). Returns null when nothing is unpriced.
 */
export function unpricedStatement(ordersWithoutPricing) {
  const n = Number(ordersWithoutPricing) || 0
  if (n <= 0) return null
  return n === 1
    ? 'Merchant/Courier rates have not been calculated for 1 order. This order has not been factored in the profitability calculations. The detailed report of orders with calculation errors can be downloaded below.'
    : `Merchant/Courier rates have not been calculated for ${count(n)} orders. These orders have not been factored in the profitability calculations. The detailed report of orders with calculation errors can be downloaded below.`
}

/**
 * Order-level CSV from the backend (the mail's two attachments): priced=true → ORDER_LEVEL_PROFITABILITY,
 * priced=false → ORDER_LEVEL_DETAILS_WITHOUT_PRICING. Saves the blob; throws the axios error so the caller can toast
 * 409 (export slots busy) / 400 (range too wide) itself.
 */
export async function exportFreightOrdersCsv(scope, priced) {
  const res = await post('finance/freight-profitability/orders/export', { ...scope, priced }, { responseType: 'blob' })
  const fallback = `PROSHIP_REPORT_${priced ? 'ORDER_LEVEL_PROFITABILITY' : 'ORDER_LEVEL_DETAILS_WITHOUT_PRICING'}_FOR_${scope.segment}_${scope.from}${scope.from === scope.to ? '' : '_' + scope.to}.csv`
  saveBlob(res.data, filenameFromResponse(res, fallback))
}

/** One message for a failed order-level export: 409 = two exports already running, else the scope hint. */
export function exportErrorMessage(e) {
  return e?.response?.status === 409
    ? 'Two exports are already running on the server. Try again in a minute.'
    : 'Export failed. Narrow the date range (7 days, or 31 days for a single merchant) and try again.'
}

/** Whole rupees, Indian grouping, sign kept: -1,23,549 → "-₹1,23,549". Zero shows as "₹0". */
export function inr(v) {
  if (v === null || v === undefined || Number.isNaN(Number(v))) return '—'
  const n = Math.round(Number(v))
  const abs = new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(Math.abs(n))
  return `${n < 0 ? '-' : ''}₹${abs}`
}

export function count(v) {
  if (v === null || v === undefined || Number.isNaN(Number(v))) return '—'
  return new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(Number(v))
}

/** Rupees with paise, Indian grouping: 16.7 → "₹16.70". */
export function inr2(v) {
  if (v === null || v === undefined || Number.isNaN(Number(v))) return '—'
  const n = Number(v)
  const abs = new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Math.abs(n))
  return `${n < 0 ? '-' : ''}₹${abs}`
}

/** A change in whole rupees with its sign: "+₹1,234", "-₹56", "₹0". */
export function signedInr(v) {
  if (v === null || v === undefined || Number.isNaN(Number(v))) return '—'
  const n = Math.round(Number(v))
  return n > 0 ? `+${inr(n)}` : inr(n)
}

/** Whole percent as in the mail: "-11 %". */
export function pct(v) {
  if (v === null || v === undefined || Number.isNaN(Number(v))) return '—'
  return `${Math.round(Number(v))} %`
}

/** Two-decimal percent as the CEO-format mails print it: "17.02 %". */
export function pct2(v) {
  if (v === null || v === undefined || Number.isNaN(Number(v))) return '—'
  return `${Number(v).toFixed(2)} %`
}

/** a / b × 100 with two decimals; null when b is zero. */
export function share(a, b) {
  const den = Number(b) || 0
  return den === 0 ? null : (Number(a) || 0) * 100 / den
}

export function dayStr(d) { return format(d, 'yyyy-MM-dd') }

export function quickRanges() {
  const today = new Date()
  const yesterday = subDays(today, 1)
  const lastWeekDay = subDays(today, 7)
  return [
    { label: 'Yesterday', from: yesterday, to: yesterday },
    { label: 'Last 7 days', from: subDays(yesterday, 6), to: yesterday },
    // The weekly mail's week: Monday to Sunday, compared with the week before.
    { label: 'Last week (Mon–Sun)', from: startOfWeek(lastWeekDay, { weekStartsOn: 1 }), to: endOfWeek(lastWeekDay, { weekStartsOn: 1 }) },
    { label: 'Month to date', from: startOfMonth(today), to: yesterday < startOfMonth(today) ? today : yesterday },
    { label: 'Last 30 days', from: subDays(yesterday, 29), to: yesterday },
  ]
}

/** Client-side CSV for the brand/courier tables. rows: array of objects, columns: [{key,label}]. */
export function downloadRowsAsCsv(rows, columns, filename) {
  const esc = (v) => {
    const s = v === null || v === undefined ? '' : String(v)
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
  }
  const lines = [columns.map(c => esc(c.label)).join(',')]
  rows.forEach(r => lines.push(columns.map(c => esc(r[c.key])).join(',')))
  saveBlob(new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' }), filename)
}

export function saveBlob(blob, filename) {
  const url = window.URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  window.URL.revokeObjectURL(url)
}

/** Pull the filename the backend set in Content-Disposition, else fall back. */
export function filenameFromResponse(res, fallback) {
  const cd = res?.headers?.['content-disposition'] || ''
  const m = /filename="?([^";]+)"?/.exec(cd)
  return m ? m[1] : fallback
}
